// OrderPanel — BUY/SELL order form modal (Angel One light theme)
import React, { useState } from 'react';
import { useMindfulOrderManager } from '../../../hooks/useMindfulOrderManager';
import { useTrading } from '../../../context/TradingContext';
import { PRODUCT_LABELS, ORDER_TYPE_LABELS } from '../../../config/angelOne';
import { FaTimes } from 'react-icons/fa';

export default function OrderPanel({ instrument, side, onClose }) {
  const { getQuote } = useTrading();
  const { submitOrder, loading, error } = useMindfulOrderManager();
  const quote = getQuote(instrument.exchange, instrument.token);

  const [form, setForm] = useState({
    transactiontype: side || 'BUY',
    ordertype: 'MARKET',
    producttype: 'INTRADAY',
    quantity: '1',
    price: '',
    triggerprice: '',
    variety: 'NORMAL',
    duration: 'DAY',
    squareoff: '',
    stoploss: '',
    trailingStoploss: '',
  });
  const [result, setResult] = useState(null);

  const isBuy = form.transactiontype === 'BUY';
  const needsPrice = form.ordertype === 'LIMIT' || form.ordertype === 'STOPLOSS_LIMIT';
  const needsTrigger = form.ordertype === 'STOPLOSS_LIMIT' || form.ordertype === 'STOPLOSS_MARKET';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const orderData = {
      variety: form.variety,
      tradingsymbol: instrument.symbol || instrument.tradingsymbol,
      symboltoken: instrument.token,
      transactiontype: form.transactiontype,
      exchange: instrument.exchange,
      ordertype: form.ordertype,
      producttype: form.producttype,
      duration: form.duration,
      quantity: form.quantity,
      ...(needsPrice && form.price ? { price: form.price } : {}),
      ...(needsTrigger && form.triggerprice ? { triggerprice: form.triggerprice } : {}),
      ...(form.variety === 'ROBO' ? {
        squareoff: form.squareoff,
        stoploss: form.stoploss,
        trailingStoploss: form.trailingStoploss,
      } : {}),
    };

    const res = await submitOrder(orderData);
    setResult(res);
    if (res?.status) {
      setTimeout(onClose, 1500);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl border border-gray-200" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`px-5 py-3 rounded-t-xl flex items-center justify-between ${
          isBuy ? 'bg-green-50 border-b border-green-200' : 'bg-red-50 border-b border-red-200'
        }`}>
          <div>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-bold ${isBuy ? 'text-green-600' : 'text-red-600'}`}>
                {form.transactiontype}
              </span>
              <span className="text-sm font-semibold text-gray-900">
                {instrument.symbol || instrument.tradingsymbol}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[11px] text-gray-500">{instrument.exchange}</span>
              {quote && <span className="text-[11px] text-gray-600 font-mono">LTP: ₹{quote.ltp}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Buy/Sell Toggle */}
          <div className="grid grid-cols-2 gap-1 p-1 bg-gray-100 rounded-lg">
            {['BUY', 'SELL'].map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setForm({ ...form, transactiontype: t })}
                className={`py-2.5 rounded-md text-xs font-bold transition-all ${
                  form.transactiontype === t
                    ? (t === 'BUY' ? 'bg-green-600 text-white shadow-sm' : 'bg-red-600 text-white shadow-sm')
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Product & Order Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-gray-600 block mb-1">Product</label>
              <select
                value={form.producttype}
                onChange={e => setForm({ ...form, producttype: e.target.value })}
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Object.entries(PRODUCT_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-medium text-gray-600 block mb-1">Order Type</label>
              <select
                value={form.ordertype}
                onChange={e => setForm({ ...form, ordertype: e.target.value })}
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Object.entries(ORDER_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Quantity */}
          <div>
            <label className="text-[11px] font-medium text-gray-600 block mb-1">Quantity</label>
            <input
              type="number"
              value={form.quantity}
              onChange={e => setForm({ ...form, quantity: e.target.value })}
              min="1"
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-mono text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Price */}
          {needsPrice && (
            <div>
              <label className="text-[11px] font-medium text-gray-600 block mb-1">Price</label>
              <input
                type="number"
                step="0.05"
                value={form.price}
                onChange={e => setForm({ ...form, price: e.target.value })}
                placeholder={quote ? `LTP: ${quote.ltp}` : ''}
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-mono text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Trigger Price */}
          {needsTrigger && (
            <div>
              <label className="text-[11px] font-medium text-gray-600 block mb-1">Trigger Price</label>
              <input
                type="number"
                step="0.05"
                value={form.triggerprice}
                onChange={e => setForm({ ...form, triggerprice: e.target.value })}
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-mono text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Error / Result */}
          {error && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
          {result?.status && (
            <p className="text-xs text-green-600 bg-green-50 px-3 py-2 rounded-lg">
              Order placed! ID: {result.data?.orderid}
            </p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded-lg text-sm font-bold transition-all text-white ${
              isBuy
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-red-600 hover:bg-red-700'
            } ${loading ? 'opacity-50' : ''}`}
          >
            {loading ? 'Placing...' : `${form.transactiontype} ${instrument.symbol || instrument.tradingsymbol}`}
          </button>
        </form>
      </div>
    </div>
  );
}
